import {
  Bell,
  CalendarDays,
  Briefcase,
  Eye,
  LayoutGrid,
  LogOut,
  Monitor,
  Moon,
  Search,
  SlidersHorizontal,
  Sun,
  Truck,
  Users,
  Wallet,
  History,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router';

import { Button } from '@/components/ui/button';
import { useCompanyBasics, useImpersonation, useLogout, useSession } from '@/features/auth/session';
import { useDashboardBadge } from '@/features/dashboard/useDashboard';
import { cn } from '@/lib/cn';
import { fmtClock, nowInTz } from '@/lib/dates';
import { initials } from '@/lib/format';
import { format } from 'date-fns';

import { ConnectionBar } from './ConnectionBar';
import { useTheme, type ThemePreference } from './theme';

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutGrid;
  end?: boolean;
  hidePhone?: boolean;
  badge?: number;
}

function Brand() {
  return (
    <div className="nav-brand flex items-center gap-2.5 px-2.5 pt-1.5 pb-5 font-cond text-[19px] font-bold text-white">
      <span className="relative size-[26px] flex-none rounded-[7px] bg-orange">
        <span className="absolute right-1.5 bottom-1.5 left-1.5 h-[5px] rounded-sm bg-nav" />
      </span>
      <span className="nav-brand-text">SiteDay</span>
    </div>
  );
}

function NavRail() {
  const { user } = useSession();
  const { companyName } = useCompanyBasics();
  const badge = useDashboardBadge();
  const isOffice = user.role === 'owner' || user.role === 'admin';

  const items: NavItem[] = isOffice
    ? [
        { to: '/', label: 'Dashboard', icon: LayoutGrid, end: true },
        { to: '/jobs', label: 'Jobs', icon: Briefcase },
        { to: '/rollup', label: 'Weekly roll-up', icon: CalendarDays },
        { to: '/people', label: 'People', icon: Users, badge: badge.people },
        { to: '/equipment', label: 'Equipment', icon: Truck },
        { to: '/quickbooks', label: 'QuickBooks', icon: Wallet, hidePhone: true },
        { to: '/settings', label: 'Settings', icon: SlidersHorizontal, hidePhone: true },
      ]
    : [{ to: '/history', label: 'History', icon: History }];

  return (
    <nav
      className="app-nav flex flex-col overflow-hidden bg-nav px-2.5 pt-3.5 pb-3 text-nav-ink"
      aria-label="Main"
    >
      <Brand />
      {items.map((it) => {
        const Icon = it.icon;
        return (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.end ?? false}
            className={({ isActive }) =>
              cn(
                'nav-link relative flex items-center gap-[11px] rounded-lg px-3 py-2.5 text-sm font-medium whitespace-nowrap text-nav-ink hover:bg-white/5 hover:text-white',
                isActive &&
                  'active bg-nav2 font-bold text-white before:absolute before:top-2 before:bottom-2 before:-left-2.5 before:w-1 before:rounded-r-[3px] before:bg-orange before:content-[""]',
                it.hidePhone && 'nav-hide-phone',
              )
            }
          >
            <Icon className="size-[18px] flex-none opacity-85" />
            <span className="nav-label">{it.label}</span>
            {it.badge ? (
              <span className="nav-badge ml-auto inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-bad px-1.5 text-[11px] font-extrabold text-on-status">
                {it.badge}
              </span>
            ) : null}
          </NavLink>
        );
      })}
      <div className="nav-spacer flex-1" />
      <div className="nav-tenant flex items-center gap-2.5 border-t border-nav-line px-2.5 pt-3 pb-1 text-xs text-nav-ink2">
        <span className="flex size-[30px] flex-none items-center justify-center rounded-full bg-orange text-[11px] font-extrabold text-on-orange">
          {initials(user.name)}
        </span>
        <div className="nav-tenant-text min-w-0">
          <b className="block truncate font-semibold text-nav-ink-hi">{user.name}</b>
          <span className="block truncate">{companyName}</span>
        </div>
      </div>
    </nav>
  );
}

function ThemeMenuItem({
  value,
  label,
  icon: Icon,
}: {
  value: ThemePreference;
  label: string;
  icon: typeof Sun;
}) {
  const { preference, setPreference } = useTheme();
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={preference === value}
      onClick={() => setPreference(value)}
      className={cn(
        'flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left hover:bg-bg2',
        preference === value && 'bg-bg2 font-bold',
      )}
    >
      <Icon className="size-4" /> {label}
    </button>
  );
}

function UserMenu() {
  const { user } = useSession();
  const logout = useLogout();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="User menu"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 rounded-lg px-1 py-0.5 font-semibold whitespace-nowrap hover:bg-bg2"
      >
        <span className="flex size-[30px] items-center justify-center rounded-full bg-orange text-xs font-extrabold text-on-orange">
          {initials(user.name)}
        </span>
        <span className="top-user-name">{user.name}</span>
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 z-40 mt-1.5 w-52 rounded-xl border border-line2 bg-surface p-1.5 text-[13px] shadow-lg2"
        >
          <div className="px-2.5 py-1.5 text-xs text-ink3">
            <b className="block text-ink">{user.name}</b>
            {user.email ?? user.phone} · {user.role}
          </div>
          <div className="my-1 border-t border-line2" />
          <div className="eyebrow px-2.5 pt-1 pb-0.5">Theme</div>
          <ThemeMenuItem value="light" label="Light" icon={Sun} />
          <ThemeMenuItem value="dark" label="Dark" icon={Moon} />
          <ThemeMenuItem value="system" label="System" icon={Monitor} />
          <div className="my-1 border-t border-line2" />
          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left hover:bg-bg2"
            onClick={() => logout.mutate()}
          >
            <LogOut className="size-4" /> Sign out
          </button>
        </div>
      ) : null}
    </div>
  );
}

function TopBar() {
  const { companyName, tz } = useCompanyBasics();
  const badge = useDashboardBadge();
  const navigate = useNavigate();
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(id);
  }, []);
  const local = nowInTz(tz, now);

  return (
    <header className="flex items-center gap-3 border-b border-line2 bg-surface px-4 md:px-[22px]">
      <span className="font-cond text-[17px] font-bold whitespace-nowrap">{companyName}</span>
      <span className="top-date text-[13px] whitespace-nowrap text-ink3 num">
        {format(local, 'EEE, MMM d, yyyy · h:mm a')} {tzAbbrev(tz, now)}
      </span>
      <button
        type="button"
        className="top-search ml-auto flex h-[34px] min-w-60 items-center gap-2 rounded-lg border border-line bg-surface2 px-3 text-[13px] text-ink3"
        onClick={() => navigate('/jobs')}
        aria-label="Find a job"
      >
        <Search className="size-4" />
        <span>Find a job, person or day…</span>
        <kbd className="ml-auto rounded border border-line bg-surface px-1.5 text-[11px]">/</kbd>
      </button>
      <button
        type="button"
        className="relative flex size-[34px] items-center justify-center rounded-lg border border-line bg-surface text-ink2"
        aria-label={badge.missing ? `${badge.missing} missing submissions` : 'Notifications'}
        onClick={() => navigate('/?missing=1')}
      >
        <Bell className="size-[18px]" />
        {badge.missing ? (
          <i className="absolute -top-1.5 -right-1.5 flex size-4 items-center justify-center rounded-full bg-bad text-[10px] font-extrabold not-italic text-on-status">
            {badge.missing}
          </i>
        ) : null}
      </button>
      <UserMenu />
    </header>
  );
}

function tzAbbrev(tz: string, now: Date): string {
  try {
    return (
      new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' })
        .formatToParts(now)
        .find((p) => p.type === 'timeZoneName')?.value ?? ''
    );
  } catch {
    return '';
  }
}

/** Persistent banner for a Fuorix support session (doc 13 "Support panel"): the API refuses every write. */
function SupportBanner() {
  const { impersonating, supportAdminName } = useImpersonation();
  const logout = useLogout();
  if (!impersonating) return null;
  return (
    <div
      role="status"
      data-testid="support-banner"
      className="mb-4 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-warn bg-warn-bg px-3.5 py-2 text-[13px] font-semibold text-ink"
    >
      <Eye className="size-4 text-warn" aria-hidden />
      <span>
        Support view · read-only · signed in as Fuorix support
        {supportAdminName ? ` (${supportAdminName})` : ''}
      </span>
      <button
        type="button"
        className="ml-auto rounded-md border border-warn bg-surface px-2.5 py-1 text-xs font-bold hover:bg-bg2"
        onClick={() => logout.mutate()}
        disabled={logout.isPending}
      >
        Exit
      </button>
    </div>
  );
}

export function AppShell() {
  const { cutoffTime } = useCompanyBasics();
  return (
    <div className="app-shell">
      <NavRail />
      <TopBar />
      <main className="app-main" data-cutoff={fmtClock(cutoffTime)}>
        <SupportBanner />
        <ConnectionBar />
        <Outlet />
      </main>
    </div>
  );
}

export function IconButton({
  label,
  children,
  ...rest
}: React.ComponentProps<typeof Button> & { label: string }) {
  return (
    <Button aria-label={label} title={label} {...rest}>
      {children}
    </Button>
  );
}
